import { useGetSettings } from '@ipfabric/api-client/client/model/settings/settings';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { allowLeave } from '@ipfabric/ui-components/LeaveDialog/LeaveDialog';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useFormComposition } from '@ipfabric/ui-components/utils/formSnippets/useFormComposition';
import type { DiscoverySettingsObject } from 'app/features/DiscoverySettingsForms/model/DiscoverySettingsObject';
import { useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { renderQuery } from 'utils/react-query/renderQuery';
import { AuthenticationValidationContext } from '../../components/Authentication/context/AuthenticationValidationContext';
import { useAuthenticationValidation } from '../../components/Authentication/hooks/useAuthenticationValidation';
import {
  type AuthenticationSubTab,
  AuthenticationTab,
} from '../../components/Authentication/tabs/AuthenticationTab';
import { pickDiscoverySettingsFromGlobalSettings } from '../../utils/pickDiscoverySettingsFromGlobalSettings';
import { WizardNavigationButtons } from './WizardNavigationButtons';
import { WizardPageLoading } from './WizardPageLoading';
import { WizardPageTemplate } from './WizardPageTemplate';
import type { WizardPageProps } from './configWizard.types';
import { deviceCredentialsGroup } from './groups/deviceCredentialsGroup';
import { privilegedCredentialsGroup } from './groups/privilegedCredentialsGroup';
import { vendorsApiGroup } from './groups/vendorsApi';

export const DiscoveryConfigWizardPage1 = (props: WizardPageProps) => {
  const globalSettingsQuery = useGetSettings();

  return renderQuery({
    query: globalSettingsQuery,
    renderStaleDataOnMount: false,
    pending: () => <WizardPageLoading wizardStepper={props.wizardStepper} />,
    success: (data) => (
      <WizardPage1
        initialData={pickDiscoverySettingsFromGlobalSettings(data)}
        {...props}
      />
    ),
  });
};

export type DiscoveryConfigWizardGroupDisplayControl = {
  setAuthenticationSubTab: (subTab: AuthenticationSubTab) => void;
};

const groups = {
  deviceCredentials: deviceCredentialsGroup,
  privilegedCredentials: privilegedCredentialsGroup,
  vendors: vendorsApiGroup,
};

const WizardPage1 = (
  props: WizardPageProps & { initialData: DiscoverySettingsObject },
) => {
  const [authenticationSubTab, setAuthenticationSubTab] =
    useState<AuthenticationSubTab>('deviceCredentials');

  const onSubmit = useStableCallback(
    async (model: DiscoverySettingsObject) => await props.onSubmit(model),
  );

  const groupDisplayControl: DiscoveryConfigWizardGroupDisplayControl = useMemo(
    () => ({
      setAuthenticationSubTab,
    }),
    [],
  );

  const formComposition = useFormComposition({
    groups,
    initialModel: props.initialData,
    onSubmit: onSubmit,
    groupDisplayControl,
  });

  const form = formComposition.form;

  const storedDeviceCredentials = useWatch({
    control: form.control,
    name: 'credentials',
  });
  const storedVendors = useWatch({
    control: form.control,
    name: 'vendors',
  });

  const authenticationValidation = useAuthenticationValidation(
    storedDeviceCredentials,
    storedVendors,
  );

  return (
    <WizardPageTemplate
      title='Authentication'
      formComposition={formComposition}
      canNavigate={!authenticationValidation.hasError}
      {...props}
    >
      <AuthenticationValidationContext.Provider
        value={{
          ...authenticationValidation,
          setSubTab: setAuthenticationSubTab,
        }}
      >
        <AuthenticationTab
          formComposition={formComposition}
          subTab={authenticationSubTab}
          setSubTab={setAuthenticationSubTab}
        />
      </AuthenticationValidationContext.Provider>
      <Card>
        <WizardNavigationButtons
          onNext={() => {
            if (authenticationValidation.hasError) {
              setAuthenticationSubTab('deviceCredentials');
              return;
            }
            if (formComposition.form.formState.isDirty) {
              formComposition.makeHandleSubmit(() =>
                allowLeave(() => props.onNextStep()),
              )();
            } else {
              props.onNextStep();
            }
          }}
          isLoading={formComposition.form.formState.isSubmitting}
        />
      </Card>
    </WizardPageTemplate>
  );
};
